import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { JobDetail } from '@nest-recruit/shared';
import { ApiError, errorMessage } from '../../../../lib/api-client';
import { useToasts } from '../../../../stores/toasts';
import { jobKey, stageBarKey } from '../../api/jobs-api';
import { useSaveLane } from '../../hooks/save-lane';
import type { SaveState } from '../../hooks/use-step-autosave';

// JOB-13 (R3): every section reports how its saving is going, and the sticky footer shows the
// one that matters most — a failure first, then anything still saving, else "Saved".
//
// The reports live in a small store that only the footer's indicator listens to. Kept as page
// state, every keystroke's "pending" re-rendered the whole builder and the section being typed
// in, and a fast typist could chain those re-renders past React's update limit (found in the
// R3 browser check). A report that says nothing new is dropped, so typing reports once.

export interface SaveReports {
  report(source: string, state: SaveState): void;
  /** A section with a save waiting on a pause registers how to send it now; null on leaving. */
  setFlush(source: string, flush: (() => Promise<void>) | null): void;
  /** Sends every save still waiting on a pause ("Open job" does this first). */
  flushAll(): Promise<void>;
  /**
   * A section whose save is a deliberate button (the ideal candidate: saving can spend money) registers how to save what
   * is typed and not yet saved; null once nothing is. "Open job" asks before going ahead without it.
   */
  setUnsaved(source: string, save: (() => Promise<boolean>) | null): void;
  /** Saves each unsaved section; true when every one was saved. */
  saveUnsaved(): Promise<boolean>;
  hasUnsaved(): boolean;
  subscribe(listener: () => void): () => void;
  /** The same object until something changes, as `useSyncExternalStore` needs. */
  overall(): SaveState;
}

export const SaveReportContext = createContext<SaveReports | null>(null);

export function useReportSaveState(source: string, state: SaveState, flush?: () => Promise<void>): void {
  const reports = useContext(SaveReportContext);
  useEffect(() => {
    reports?.report(source, state);
  }, [reports, source, state]);
  useEffect(() => {
    if (!reports || !flush) return undefined;
    reports.setFlush(source, flush);
    return () => reports.setFlush(source, null);
  }, [reports, source, flush]);
}

/** A section with something typed and not yet saved says so while it is, and takes it back when it is saved or gone. */
export function useReportUnsaved(source: string, save: (() => Promise<boolean>) | null): void {
  const reports = useContext(SaveReportContext);
  useEffect(() => {
    if (!reports || !save) return undefined;
    reports.setUnsaved(source, save);
    return () => reports.setUnsaved(source, null);
  }, [reports, source, save]);
}

export function createSaveReports(): SaveReports {
  const states = new Map<string, SaveState>();
  const flushes = new Map<string, () => Promise<void>>();
  const unsaved = new Map<string, () => Promise<boolean>>();
  const listeners = new Set<() => void>();
  let overall: SaveState = { kind: 'idle' };
  return {
    report(source: string, state: SaveState): void {
      const previous = states.get(source);
      if (previous && sameSaveState(previous, state)) return;
      states.set(source, state);
      const next = overallOf([...states.values()]);
      if (sameSaveState(next, overall)) return;
      overall = next;
      listeners.forEach((listener) => listener());
    },
    setFlush(source: string, flush: (() => Promise<void>) | null): void {
      if (flush) flushes.set(source, flush);
      else flushes.delete(source);
    },
    async flushAll(): Promise<void> {
      await Promise.all([...flushes.values()].map((flush) => flush()));
    },
    setUnsaved(source: string, save: (() => Promise<boolean>) | null): void {
      if (save) unsaved.set(source, save);
      else unsaved.delete(source);
    },
    async saveUnsaved(): Promise<boolean> {
      const results = await Promise.all([...unsaved.values()].map((save) => save()));
      return results.every(Boolean);
    },
    hasUnsaved: () => unsaved.size > 0,
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    overall: () => overall,
  };
}

/** The footer's view: re-renders only the indicator, never the page or a section. */
export function useOverallSaveState(reports: SaveReports): SaveState {
  return useSyncExternalStore(reports.subscribe, reports.overall);
}

function sameSaveState(a: SaveState, b: SaveState): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'saved' && b.kind === 'saved') return a.at.getTime() === b.at.getTime();
  if (a.kind === 'error' && b.kind === 'error') return a.message === b.message;
  return true;
}

function overallOf(states: readonly SaveState[]): SaveState {
  const failed = states.find((state) => state.kind === 'error');
  if (failed) return failed;
  if (states.some((state) => state.kind === 'saving')) return { kind: 'saving' };
  if (states.some((state) => state.kind === 'pending')) return { kind: 'pending' };
  const saved = states.flatMap((state) => (state.kind === 'saved' ? [state.at] : []));
  if (saved.length === 0) return { kind: 'idle' };
  return { kind: 'saved', at: new Date(Math.max(...saved.map((at) => at.getTime()))) };
}

/**
 * A table section (criteria, matrix): each cell's edit shows at once and is saved as the whole
 * list through the job's lane. A refused save puts the last saved rows back and says why
 * (CLAUDE.md §5a rule 1, QoL §14).
 */
export function useListSection<Row>({
  source,
  job,
  initial,
  send,
}: {
  readonly source: string;
  readonly job: JobDetail;
  readonly initial: readonly Row[];
  /** Saves the rows worth sending (named ones) with the version the lane hands over. */
  readonly send: (rows: readonly Row[], version: number) => Promise<JobDetail>;
}): {
  readonly rows: readonly Row[];
  /** Local only: a new blank row, before it has anything to save. */
  setRows(next: readonly Row[]): void;
  commit(next: readonly Row[]): Promise<void>;
} {
  const [rows, setRowsState] = useState(initial);
  const [state, setState] = useState<SaveState>({ kind: 'idle' });
  const latest = useRef(initial);
  const saved = useRef(initial);
  const lane = useSaveLane();
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  useReportSaveState(source, state);

  const setRows = useCallback((next: readonly Row[]) => {
    latest.current = next;
    setRowsState(next);
  }, []);

  const commit = useCallback(
    async (next: readonly Row[]): Promise<void> => {
      setRows(next);
      setState({ kind: 'saving' });
      try {
        const runSave = (version: number): Promise<JobDetail> => send(latest.current, version);
        const updated = lane ? await lane.run(runSave) : await runSave(job.version);
        saved.current = latest.current;
        queryClient.setQueryData(jobKey(updated.id), updated);
        void queryClient.invalidateQueries({ queryKey: stageBarKey(updated.id) });
        setState({ kind: 'saved', at: new Date() });
      } catch (error) {
        setRows(saved.current);
        setState({ kind: 'error', message: errorMessage(error), fields: error instanceof ApiError ? error.fields : {} });
        show({ tone: 'danger', message: 'That change was not saved', detail: errorMessage(error) });
        if (error instanceof ApiError && error.code === 'stale_write') await queryClient.refetchQueries({ queryKey: jobKey(job.id) });
        throw error;
      }
    },
    [setRows, send, lane, job.version, job.id, queryClient, show],
  );

  return { rows, setRows, commit };
}
